import { IConfiguration } from '../../../../domain/conf'
import { Utils } from '../../../../utils/common'
import { DOMEvents } from '../../../../utils/dom'
import { Store } from '../../../../utils/storage/keys'
import { LocalStore } from '../../../../utils/storage/local'
import { AnalyticsService } from '../../../analytics'
import { IdentityEvent } from '../../../common/events/identity'
import { PageType } from '../../../common/page-types'
import { IProfile } from '../../../common/profile'
import { IIdentityServiceAdapter } from '../adapter'
import { ArticleBlock } from '../article-block'

interface IWindow extends Window {
    Connext: any
    MG2Loader: any
    pageTag: string
}

interface INavigaPlugin {
    name: string
    initOptions: any
}

export enum PaywallLevel {
    NOT_METERED = 'not_metered',
    METERED = 'metered',
    REGISTRATION_LOCKED = 'registration_locked',
    PREMIUM_LOCKED = 'premium_locked',
}

export enum MeterLevel {
    FREE = 1,
    METERED = 2,
    PREMIUM = 3,
}

const NEW_USER_CLAIM = 'https://postmedia.com/is_new_user'
const METHOD_CLAIM = 'https://postmedia.com/method'

declare let window: IWindow

export class NavigaAuth0Adapter implements IIdentityServiceAdapter {
    private static pageType: string
    private fingerprintPlugin: INavigaPlugin
    private g2InsightsPlugin: INavigaPlugin
    private engagePlugin: INavigaPlugin

    constructor(private pageData: IConfiguration) {
        const auth0Config = pageData.identity?.auth0
        const navigaConfig = pageData.identity?.naviga

        NavigaAuth0Adapter.pageType = pageData.page?.type || ''

        // Naviga plugin configurations
        // tslint:disable-next-line:max-line-length
        // Reference: https://docs.navigaglobal.com/engage/-MGi9-KKLwnWfoD36Ot6/getting-started-with-engage/configuring-engage-on-your-website/setting-up-the-inititalization-script#initialization-script-options
        if (auth0Config && navigaConfig) {
            const attr = navigaConfig.engage?.attr
            const settingsKey = navigaConfig.engage?.settingsKey

            this.fingerprintPlugin = {
                initOptions: {
                    environment: navigaConfig.environment || '',
                    version: navigaConfig.fingerprint?.version || '',
                },
                name: 'FP',
            }

            this.g2InsightsPlugin = {
                initOptions: {
                    collectors: navigaConfig.g2Insights?.collectors || [],
                    containerId: navigaConfig.g2Insights?.containerId || '',
                    tagManager: navigaConfig.g2Insights?.tagManager || '',
                    version: navigaConfig.g2Insights?.version || '',
                },
                name: 'DL',
            }

            this.engagePlugin = {
                initOptions: {
                    authSettings: {
                        // Auth0 configurations
                        // authSettings.client_id and authSettings.redirect_uri keys required for older versions
                        // old keys to be removed after the update
                        authorizationParams: {
                            redirect_uri: `${location.origin}/auth0-redirect/`
                        },
                        clientId: auth0Config.clientId || '',
                        client_id: auth0Config.clientId || '', // required for Engage v2025.5
                        domain: auth0Config.domain || '',
                        redirect_uri: `${location.origin}/auth0-redirect/` // required for Engage v2025.5
                    },
                    clientCode: navigaConfig.engage?.clientCode || '',
                    configCode: navigaConfig.engage?.configCode || '',
                    debug: navigaConfig.debug || false,
                    environment: navigaConfig.environment || '',
                    publicEventHandlers: {
                        // List of Naviga Engage events:
                        // https://docs.navigaglobal.com/engage/-MGi9-KKLwnWfoD36Ot6/fundamentals/engage-events
                        onConversationDetermined: NavigaAuth0Adapter.handleOnConversationDeterminedEvent,
                        onInit: NavigaAuth0Adapter.handleOnInitEvent,
                        onLoggedIn: NavigaAuth0Adapter.handleOnLoggedInEvent,
                        onLoginSuccess: NavigaAuth0Adapter.handleOnLoginSuccessEvent,
                        onNotAuthorized: NavigaAuth0Adapter.handleOnNotAuthorizedEvent,
                        onPaywallShown: NavigaAuth0Adapter.handleOnPaywallShownEvent
                    },
                    siteCode: navigaConfig.engage?.siteCode || '',
                },
                name: 'NXT',
            }

            // Add attr if it exists in page-data
            if (attr) {
                this.engagePlugin.initOptions.attr = attr
            }

            // Add settingsKey if it exists in page-data
            if (settingsKey) {
                this.engagePlugin.initOptions.settingsKey = settingsKey
            }
        }
    }

    /**
     * On conversation determined event
     */
    public static handleOnConversationDeterminedEvent(eventData: any) {
        // Get the meter level: Free = 1, Metered = 2 and Premium = 3
        // https://docs.navigaglobal.com/engage/-MGi9-KKLwnWfoD36Ot6/fundamentals/engage-commands/configuration-commands
        const meterLevel = window.Connext.Storage.GetCurrentMeterLevel()

        if (meterLevel === MeterLevel.METERED) {
            const meterName = eventData.EventData?.Name || 'undefined'

            // Send meter execute event
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                meter_name: 'meter_name',
                            },
                            mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                            mp_event_type: 'UserPreference'
                        }
                    },
                    event_name: AnalyticsService.customEventNames.meterExecute,
                    meter_name: meterName // Naviga Engage conversation name
                }
            }))
        }
    }

    /**
     * On init event
     */
    public static handleOnInitEvent() {
        const signInLoader = document.querySelector('.sso-btns .sign-in-spinner')

        // Remove sign in loader
        if (signInLoader) {
            signInLoader.remove()
        }
    }

    /**
     * On login success event
     */
    public static handleOnLoginSuccessEvent() {
        // Set login success session storage flag
        // Workaround to prevent having missed login events on the redirect page
        sessionStorage.setItem(Store.NAVIGA_AUTH0_LOGIN_SUCCESS, 'true')
    }

    /**
     * On logged in event
     *
     * Fires when user is logged in
     */
    public static handleOnLoggedInEvent(eventData: any) {
        const profile = NavigaAuth0Adapter.getProfileFromEventData(eventData)
        const authProfile = eventData?.AuthProfile || {}
        const isNewLoginRegister = sessionStorage.getItem(Store.NAVIGA_AUTH0_LOGIN_SUCCESS) === 'true' || false

        /*
          New user and method claims are coming from Auth0 post-login trigger

          Post-login trigger code to get the new user claim:

          exports.onExecutePostLogin = async (event, api) => {
                const namespace = "https://postmedia.com/";
                const userId = event.user.user_id;
                const isImportedUser = /auth0\|lr/.test(userId);

                // A brand new user has login count of 1 and no linked accounts
                // Exclude imported users as well
                const isNewUser = !!(event.stats.logins_count === 1
                    && event.user.identities.length === 1 && !isImportedUser);

                // Add custom claims
                api.idToken.setCustomClaim(`${namespace}is_new_user`, isNewUser);
            };
         */

        const isNewUser = (NEW_USER_CLAIM in authProfile) ? authProfile[NEW_USER_CLAIM] : false
        const method = (METHOD_CLAIM in authProfile) ? authProfile[METHOD_CLAIM] : 'unknown'

        if (!profile || NavigaAuth0Adapter.pageType === PageType.AUTH0_REDIRECT) {
            return
        }

        if (!isNewLoginRegister) {
            // Already logged in, just update the profile
            document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, profile))
            return
        }

        // Remove login success event flag
        sessionStorage.removeItem(Store.NAVIGA_AUTH0_LOGIN_SUCCESS)

        // Update profile
        if (isNewUser) {
            document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, {
                ...profile,
                isRegistration: true
            }))
        } else {
            document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, {
                ...profile,
                isLogin: true
            }))
        }

        // Wait for profile to be updated
        const onIdentityProfileUpdated = () => {
            // Remove identity profile updated event listener
            document.removeEventListener(IdentityEvent.IDENTITY_PROFILE_UPDATED, onIdentityProfileUpdated)

            if (isNewUser) {
                document.dispatchEvent(new CustomEvent(IdentityEvent.NEW_REGISTRATION, {
                    detail: {
                        email: profile.email,
                        uuid: profile.uuid
                    }
                }))

                // Send registration event
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                    detail: {
                        control_fields: {
                            mparticle: {
                                keys: {
                                    integration_name: 'integration_name',
                                    registration_method: 'registration_method',
                                },
                                mp_event_type: 'UserContent'
                            }
                        },
                        event_name: AnalyticsService.customEventNames.registration,
                        integration_name: 'Naviga_Auth0',
                        registration_method: method,
                    }
                }))
            } else {
                document.dispatchEvent(new CustomEvent(IdentityEvent.IDENTITY_LOGIN, {
                    detail: {
                        email: profile.email,
                        uuid: profile.uuid
                    }
                }))

                // Send sign in event
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                    detail: {
                        control_fields: {
                            mparticle: {
                                keys: {
                                    integration_name: 'integration_name',
                                    sign_in_method: 'sign_in_method',
                                },
                                mp_event_type: 'UserContent'
                            }
                        },
                        event_name: AnalyticsService.customEventNames.signin,
                        integration_name: 'Naviga_Auth0',
                        sign_in_method: method,
                    }
                }))
            }
        }

        document.addEventListener(IdentityEvent.IDENTITY_PROFILE_UPDATED, onIdentityProfileUpdated)
    }

    /**
     * On not authorized event
     *
     * Fires when user is not logged in
     */
    public static handleOnNotAuthorizedEvent() {
        const profile = NavigaAuth0Adapter.emptyProfile()

        // Update with empty profile
        document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, profile))
    }

    /**
     * On paywall shown event
     */
    public static handleOnPaywallShownEvent(eventData: any) {
        // Get the meter level: Free = 1, Metered = 2 and Premium = 3
        const meterLevel = window.Connext.Storage.GetCurrentMeterLevel()
        const meterName = eventData.Conversation?.Name || 'undefined'
        const modalName = eventData.EventData?.Name || 'undefined'

        // Remove widgets and ads below the featured image
        ArticleBlock.removeWidgetsAndAds()

        // Send metered modal view event
        if (meterLevel === MeterLevel.METERED) {
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                meter_name: 'meter_name',
                                modal_name: 'modal_name'
                            },
                            mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                            mp_event_type: 'UserPreference'
                        }
                    },
                    event_name: AnalyticsService.customEventNames.modalView,
                    meter_name: meterName, // Naviga Engage conversation name
                    modal_name: modalName  // Naviga Engage paywall action name
                }
            }))
        }

        // Send premium modal view event
        if (meterLevel === MeterLevel.PREMIUM) {
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                modal_name: 'modal_name'
                            },
                            mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                            mp_event_type: 'UserPreference'
                        }
                    },
                    event_name: AnalyticsService.customEventNames.modalView,
                    modal_name: modalName // Naviga Engage paywall action name
                }
            }))
        }
    }

    /**
     * Get profile from Naviga event data
     */
    private static getProfileFromEventData(eventData: any): IProfile| undefined {
        const authProfile = eventData.AuthProfile
        const subscriptionData = eventData.MG2AccountData
        let profile: IProfile

        // Get Auth0 profile details
        if (authProfile) {
            profile = {
                adLight: false,
                email: authProfile.email,
                emailConfirmed: authProfile.email_verified,
                name: authProfile.name,
                uuid: authProfile.sub
            } as IProfile

            // Get Naviga profile details
            if (subscriptionData) {
                const isPremium = subscriptionData.DigitalAccess?.AccessLevel?.IsPremium

                if (isPremium) {
                    // Premium digital access
                    profile.adLight = true
                }
            }
        }

        return profile
    }

    /**
     * Create an empty profile
     */
    private static emptyProfile(): IProfile {
        const profile = {
            adLight: false,
            email: undefined,
            emailConfirmed: undefined,
            name: undefined,
            uuid: undefined,
        } as IProfile

        return profile
    }

    /**
     * Initalize Naviga plugins
     */
    public init() {
        const logoutButton = document.querySelector('[data-mg2-action=logout]')
        if (window.MG2Loader) {
            // Load Naviga plugins
            window.MG2Loader.init(
                {
                    plugins: [
                        this.fingerprintPlugin,
                        this.g2InsightsPlugin,
                        this.engagePlugin
                    ]
                }
            )
        }

        // Add paywall level page tag
        this.setPaywallLevel()

        // Logout button click event
        if (logoutButton) {
            logoutButton.addEventListener('click', () => {
                this.signOut()
            })
        }
    }

    /**
     * Sign out
     */
    public signOut() {
        const localProfile = LocalStore.get<IProfile>(Store.ID)
        const anonymous = NavigaAuth0Adapter.emptyProfile()

        // Clear profile
        if (localProfile) {
            anonymous.id = localProfile.id
        }

        // Update profile to anonymous
        document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, {
            ...anonymous,
            isLogin: undefined,
            isRegistration: undefined
        }))

        // Send sign out analytics event
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.signout,
                integration_name: 'Naviga_Auth0',
            }
        }))

        // Sign out successful
        document.dispatchEvent(new CustomEvent(IdentityEvent.IDENTITY_SIGNOUT))
    }

    /**
     * Check if there is an auth0_sub cookie
     */
    public hasSession(): boolean {
        const auth0Cookie = (document.cookie.split(';').find((c) =>
                c.trim().startsWith('auth0_sub')) || '').trim().split('=').pop() || undefined
        return !!auth0Cookie
    }

    /**
     * Define paywall level base on page data
     */
    public setPaywallLevel() {
        const page = this.pageData.page
        let pageTag: string

        if (page) {
            if (page.isPremium) {
                // premium locked
                pageTag = PaywallLevel.PREMIUM_LOCKED
            } else if (page.isRegistration) {
                // registration only
                pageTag = PaywallLevel.REGISTRATION_LOCKED
            } else if (page.isPaywallWhitelisted) {
                // non-metered content
                pageTag = PaywallLevel.NOT_METERED
            } else {
                // metered content
                pageTag = PaywallLevel.METERED
            }

            window.pageTag = pageTag
        }
    }
}
